import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Save, X, Trash2, Mic, Square, Play, Pause, Volume2, Upload, ChevronRight, BookOpen, FolderOpen, FolderPlus, PlusCircle, ImagePlus, Download, ChevronDown, Check, XCircle, Info, Loader2, CalendarDays, AlertTriangle, RotateCcw, Paperclip, Leaf } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import ScrollDatePicker from './ScrollDatePicker';
import axios from 'axios';
import AudioPlayer from './AudioPlayer';
import AudioErrorBoundary from './AudioErrorBoundary';
import ThemedDialog from './ThemedDialog';
import { useRecordingContext } from '../contexts/RecordingContext';
import { useNavigationDirection } from '../contexts/NavigationContext';
import { LinkifyText } from './LinkifyText';
import { ProgressBar } from './ui/ProgressBar';
import { useMobileDisplayCheck } from '../hooks/useMobileDisplayCheck';
import GuestSignupModal from './GuestSignupModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: get image URL for a given variant (thumb/medium/full)
// Uses pre-signed URLs from API response (images[].thumbUrl, mediumUrl, fullUrl)
const getImageUrl = (entry, idx, variant = 'medium') => {
  if (entry.images && entry.images[idx]) {
    const img = entry.images[idx];
    // Use pre-signed URLs from API response
    if (variant === 'thumb' && img.thumbUrl) return img.thumbUrl;
    if (variant === 'medium' && img.mediumUrl) return img.mediumUrl;
    if (variant === 'full' && img.fullUrl) return img.fullUrl;
    // Fallback to proxy endpoint if pre-signed URLs not available
    return `${API}/journal/images/${img.id}?variant=${variant}`;
  }
  // Fallback: old format (shouldn't happen anymore as API enriches all entries)
  const filename = entry.image_filenames?.[idx];
  if (filename) return `${API}/journal/images/${filename}?variant=${variant}`;
  return null;
};

const getImageId = (entry, idx) => {
  if (entry.images && entry.images[idx]) return entry.images[idx].id;
  return entry.image_filenames?.[idx] || null;
};

const getImageDimensions = (entry, idx) => {
  if (entry.images && entry.images[idx]) {
    return { width: entry.images[idx].width, height: entry.images[idx].height };
  }
  return null;
};

// Helper: get download URL for image (returns absolute URL for JPEG conversion endpoint)
const getDownloadUrl = (entry, idx) => {
  if (entry.images && entry.images[idx]) {
    const img = entry.images[idx];
    // downloadUrl from API is relative path - prepend backend URL
    if (img.downloadUrl) {
      // If already absolute URL, use as-is; otherwise prepend API base
      if (img.downloadUrl.startsWith('http')) return img.downloadUrl;
      return `${BACKEND_URL}${img.downloadUrl}`;
    }
    // Fallback to download endpoint
    return `${API}/journal/images/${img.id}/download`;
  }
  const filename = entry.image_filenames?.[idx];
  if (filename) return `${API}/journal/images/${filename}/download`;
  return null;
};

// Auth handled via Bearer token in axios interceptor (App.js)

// Direction-aware page variants for swipe transitions
const getPageVariants = (direction) => ({
  initial: { opacity: 0, x: direction * 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: direction * -30 }
});

const pageTransition = { duration: 0.25, ease: 'easeOut' };

function JournalPage({ user }) {
  const { direction } = useNavigationDirection();
  
  // Mobile display check - auto-reload if page doesn't render properly
  useMobileDisplayCheck('[data-testid="journal-page"]');
  const [content, setContent] = useState('');
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [highlightedEntry, setHighlightedEntry] = useState(null);
  const [expandedEntries, setExpandedEntries] = useState({});
  const [transcribingId, setTranscribingId] = useState(null);
  const [transcribeProgress, setTranscribeProgress] = useState(0);
  const transcribeIntervalRef = useRef(null);
  const [folders, setFolders] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [assigningId, setAssigningId] = useState(null);
  const [assignDropdownOpen, setAssignDropdownOpen] = useState(null);
  const [inlineFolderName, setInlineFolderName] = useState('');
  const [isCreatingInlineFolder, setIsCreatingInlineFolder] = useState(false);
  const [pendingSelections, setPendingSelections] = useState(new Set());
  const [initialSelections, setInitialSelections] = useState(new Set());
  const [pendingNewFolders, setPendingNewFolders] = useState([]);
  const [isSavingFolders, setIsSavingFolders] = useState(false);
  const [isLoadingEntries, setIsLoadingEntries] = useState(true);
  const [draftSaved, setDraftSaved] = useState(false);
  const [recordingSaved, setRecordingSaved] = useState(false);
  const [showGuestSignup, setShowGuestSignup] = useState(false);
  const assignDropdownRef = useRef(null);
  const newEntryRef = useRef(null);
  const draftTimerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Global recording context - disables swipe navigation while recording
  const { setIsRecording: setGlobalRecording } = useRecordingContext();
  
  const MAX_PREVIEW_LENGTH = 450;
  
  // Auto-save draft
  useEffect(() => {
    if (content.trim()) {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      draftTimerRef.current = setTimeout(() => {
        localStorage.setItem('noyo_journal_draft', content);
        setDraftSaved(true);
        setTimeout(() => setDraftSaved(false), 2000);
      }, 1500);
    } else {
      localStorage.removeItem('noyo_journal_draft');
    }
    return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
  }, [content]);

  // Load draft on mount
  useEffect(() => {
    const draft = localStorage.getItem('noyo_journal_draft');
    if (draft && !content) setContent(draft);
  }, []);
  
  const toggleEntryExpansion = (entryId) => {
    setExpandedEntries(prev => ({
      ...prev,
      [entryId]: !prev[entryId]
    }));
  };
  
  const [audioDropdownOpen, setAudioDropdownOpen] = useState(false);
  const audioDropdownRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionProgress, setTranscriptionProgress] = useState(0); // For transcription progress bar
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedAudio, setUploadedAudio] = useState(null); // {entry_id, audio_filename}
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [selectedImages, setSelectedImages] = useState([]); // {file, preview}
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  // Lightbox state: { imageUrl: string, downloadUrl: string|null }
  const [lightboxImage, setLightboxImage] = useState(null);
  const [dialogConfig, setDialogConfig] = useState({ open: false, title: '', message: '', type: 'alert', onConfirm: null, onCancel: null });
  const [extractedFoldersToast, setExtractedFoldersToast] = useState(null); // {count, folders: [{name, id}]}
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [welcomeStreak, setWelcomeStreak] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [showStreakPanel, setShowStreakPanel] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [pickedTheme, setPickedTheme] = useState(() => {
    if (document.documentElement.classList.contains('sunset')) return 'sunset';
    if (document.documentElement.classList.contains('twilight')) return 'twilight';
    return 'default';
  });
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const pendingNavigationRef = useRef(null);
  const [customDate, setCustomDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Check if there are unsaved changes (content in textarea or recording in progress)
  const hasUnsavedChanges = useCallback(() => {
    return content.trim().length > 0 || isRecording || audioBlob !== null || selectedImages.length > 0;
  }, [content, isRecording, audioBlob, selectedImages]);

  // Handle browser back/refresh with beforeunload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Custom navigation with confirmation
  const safeNavigate = useCallback((path) => {
    if (hasUnsavedChanges()) {
      pendingNavigationRef.current = path;
      setShowLeaveConfirm(true);
    } else {
      navigate(path);
    }
  }, [hasUnsavedChanges, navigate]);

  const confirmLeave = () => {
    setShowLeaveConfirm(false);
    if (pendingNavigationRef.current) {
      navigate(pendingNavigationRef.current);
      pendingNavigationRef.current = null;
    }
  };

  const cancelLeave = () => {
    setShowLeaveConfirm(false);
    pendingNavigationRef.current = null;
  };

  // Show welcome popup - different messaging for new vs returning users (max once per day)
  // Skip for users who just joined via QR/invite link
  useEffect(() => {
    if (localStorage.getItem('noyo_qr_join_folder_id') || localStorage.getItem('pending_invite_link')) return;

    const todayKey = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const lastWelcomed = localStorage.getItem('noyo_last_welcomed_date');
    if (lastWelcomed === todayKey) return; // Already shown today

    const sessionKey = 'noyo_session_welcomed';
    const hasBeenWelcomed = sessionStorage.getItem(sessionKey);
    if (!hasBeenWelcomed) {
      const loadWelcome = async () => {
        try {
          const entriesRes = await axios.get(`${API}/journal`);
          const hasEntries = entriesRes.data && entriesRes.data.length > 0;
          setIsNewUser(!hasEntries);
          
          if (hasEntries) {
            const res = await axios.get(`${API}/streak`);
            setWelcomeStreak(res.data);
            setStreakData(res.data);
          }
          setShowWelcomeBack(true);
          sessionStorage.setItem(sessionKey, 'true');
          localStorage.setItem('noyo_last_welcomed_date', todayKey);
        } catch (e) {}
      };
      loadWelcome();
    }
  }, []);

  // Auto-start recording if navigated with startRecording state (from Stories page)
  useEffect(() => {
    if (location.state?.startRecording) {
      // Small delay to let the page render first
      const timer = setTimeout(() => startRecording(), 500);
      // Clear the state so it doesn't re-trigger
      window.history.replaceState({}, document.title);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Fetch streak data for the inline plant panel (always available)
  useEffect(() => {
    const fetchStreak = async () => {
      try {
        const res = await axios.get(`${API}/streak`);
        setStreakData(res.data);
      } catch (e) {
        // Retry once after a delay (auth cookies may not be ready)
        setTimeout(async () => {
          try {
            const res = await axios.get(`${API}/streak`);
            setStreakData(res.data);
          } catch (e2) {}
        }, 2000);
      }
    };
    fetchStreak();
  }, []);

  const applyTheme = (theme) => {
    document.documentElement.classList.remove('pretty', 'sunset', 'twilight');
    localStorage.removeItem('noyo_pretty');
    localStorage.removeItem('noyo_sunset');
    localStorage.removeItem('noyo_twilight');
    if (theme !== 'default') {
      document.documentElement.classList.add(theme);
      localStorage.setItem(`noyo_${theme}`, 'true');
    }
    // Twilight looks best in dark mode — auto-enable it
    if (theme === 'twilight') {
      document.documentElement.classList.add('dark');
      localStorage.setItem('noyo_dark_mode', 'true');
    }
    if (theme === 'sunset') {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('noyo_dark_mode', 'false');
    }
    setPickedTheme(theme);
  };

  const handleWelcomeDismiss = (navPath) => {
    setShowWelcomeBack(false);
    const hasSeenThemePicker = localStorage.getItem('noyo_theme_picker_seen');
    if (!hasSeenThemePicker) {
      setShowThemePicker(true);
    }
    if (navPath) navigate(navPath);
  };

  const handleThemePickerDone = () => {
    localStorage.setItem('noyo_theme_picker_seen', 'true');
    setShowThemePicker(false);
  };


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (audioDropdownRef.current && !audioDropdownRef.current.contains(e.target)) {
        setAudioDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const entryId = params.get('entry');
    if (entryId) {
      setHighlightedEntry(entryId);
      setTimeout(() => {
        const element = document.querySelector(`[data-entry-id="${entryId}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
      setTimeout(() => setHighlightedEntry(null), 3500);
    }
  }, [location.search, entries]);

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      selectedImages.forEach(img => URL.revokeObjectURL(img.preview));
      document.body.style.overflow = '';
    };
  }, [audioUrl]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (lightboxImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxImage]);

  // Poll for background transcription completion
  useEffect(() => {
    const processingEntries = entries.filter(e => e.transcription_status === 'processing');
    if (processingEntries.length === 0) return;
    
    const pollInterval = setInterval(async () => {
      let anyCompleted = false;
      for (const entry of processingEntries) {
        try {
          const res = await axios.get(`${API}/journal/${entry.id}/transcription-status`);
          if (res.data.status === 'completed' || res.data.status === 'failed') {
            anyCompleted = true;
          }
        } catch (err) {
          // ignore
        }
      }
      if (anyCompleted) {
        await fetchEntries();
      }
    }, 5000); // Poll every 5 seconds
    
    return () => clearInterval(pollInterval);
  }, [entries]);

  const fetchEntries = async () => {
    setIsLoadingEntries(true);
    try {
      // Add timestamp to prevent caching of signed URLs
      const ts = Date.now();
      const [entriesRes, foldersRes, expRes] = await Promise.all([
        axios.get(`${API}/journal?_t=${ts}`),
        axios.get(`${API}/folders`),
        axios.get(`${API}/experiences`)
      ]);
      setEntries(entriesRes.data);
      setFolders(foldersRes.data.filter(f => !f.is_suggested));
      setExperiences(expRes.data);
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setIsLoadingEntries(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000  // Lower sample rate for smaller files (Whisper works fine with 16kHz)
        } 
      });
      streamRef.current = stream;
      
      // Use lower bitrate for smaller files - 32kbps is plenty for voice
      const options = { 
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 32000  // 32kbps - good quality for voice, much smaller files
      };
      
      // Fallback if opus not supported
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'audio/webm';
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      };
      
      mediaRecorderRef.current.start(100); // Collect data every 100ms for smoother pause/resume
      setIsRecording(true);
      setGlobalRecording(true); // Disable swipe navigation
      setIsPaused(false);
      setRecordingTime(0);
      
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      setDialogConfig({ open: true, title: 'Microphone Error', message: 'Could not access microphone. Please check your permissions.', type: 'alert' });
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      const finalRecordingTime = recordingTime;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setGlobalRecording(false);
      setIsPaused(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      // Upload audio immediately after blob is ready
      const origOnStop = mediaRecorderRef.current.onstop;
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        // Immediately upload audio file directly to GCS
        await uploadAudioToGCS(blob, finalRecordingTime);
      };
    }
  };

  // Direct-to-GCS upload using resumable upload protocol
  const uploadAudioToGCS = async (blob, duration) => {
    setIsUploadingAudio(true);
    setUploadProgress(0);
    
    // Upload through the backend server (reliable, no CORS issues)
    try {
      setUploadProgress(10);
      const formData = new FormData();
      const contentType = blob.type || 'audio/webm';
      const filename = `recording.${contentType.split('/')[1] || 'webm'}`;
      formData.append('audio', blob, filename);
      
      const uploadRes = await axios.post(`${API}/journal/upload-audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(10 + Math.round((e.loaded / e.total) * 80));
        }
      });
      
      setUploadProgress(95);
      
      if (uploadRes.data.entry_id) {
        setUploadedAudio({
          entry_id: uploadRes.data.entry_id,
          audio_id: uploadRes.data.entry_id,
          audio_filename: uploadRes.data.audio_filename,
          object_path: `audios/${uploadRes.data.audio_filename}`,
          duration: duration
        });
        setRecordingSaved(true);
        setTimeout(() => setRecordingSaved(false), 3000);
        
        // Auto-transcribe short recordings (< 5 min)
        if (duration && duration < 300) {
          setIsTranscribing(true);
          setTranscriptionProgress(0);
          const progressInterval = setInterval(() => {
            setTranscriptionProgress(prev => prev >= 90 ? prev : prev + Math.random() * 15);
          }, 500);
          try {
            const transcribeForm = new FormData();
            transcribeForm.append('audio', blob, filename);
            const transcribeRes = await axios.post(`${API}/transcribe`, transcribeForm, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            setTranscriptionProgress(100);
            if (transcribeRes.data.success && transcribeRes.data.text) {
              setContent(prev => prev.trim() ? prev + '\n\n' + transcribeRes.data.text : transcribeRes.data.text);
            }
          } catch (err) {
            console.error('Auto-transcription error:', err);
          } finally {
            clearInterval(progressInterval);
            setIsTranscribing(false);
            setTranscriptionProgress(0);
          }
        }
        
        setUploadProgress(100);
        return; // Success!
      }
    } catch (error) {
      console.error('Audio upload error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save audio. Please try again.';
      setDialogConfig({ 
        open: true, 
        title: 'Audio Upload Failed', 
        message: errorMessage, 
        type: 'alert' 
      });
      setAudioBlob(null);
      setAudioUrl(null);
      setUploadedAudio(null);
    } finally {
      setIsUploadingAudio(false);
      setUploadProgress(0);
    }
  };

  // Legacy upload function (keeping for backwards compatibility with file uploads)
  const uploadAudioToBackend = async (blob, duration) => {
    // Redirect to new GCS upload
    return uploadAudioToGCS(blob, duration);
  };

  const discardRecording = async () => {
    if (uploadedAudio?.audio_filename) {
      try {
        await axios.delete(`${API}/journal/uploaded-audio/${uploadedAudio.audio_filename}`);
      } catch (err) {
        console.error('Failed to delete uploaded audio:', err);
      }
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setUploadedAudio(null);
    setRecordingTime(0);
  };

  // Discard entire journal entry (with confirmation)
  const handleDiscardEntry = () => {
    // Only show confirmation if there's something to discard
    if (!content.trim() && !audioBlob && selectedImages.length === 0 && !uploadedAudio) {
      return;
    }
    
    setDialogConfig({
      open: true,
      title: 'Discard Entry?',
      message: 'Are you sure you want to erase this entry? All text, audio recordings, and images will be permanently deleted.',
      type: 'confirm',
      onConfirm: async () => {
        setDialogConfig({ open: false });
        await performDiscardEntry();
      },
      onCancel: () => setDialogConfig({ open: false })
    });
  };

  const performDiscardEntry = async () => {
    // Delete uploaded audio from bucket if exists
    if (uploadedAudio?.audio_filename) {
      try {
        await axios.delete(`${API}/journal/uploaded-audio/${uploadedAudio.audio_filename}`);
      } catch (err) {
        console.error('Failed to delete uploaded audio:', err);
      }
    }
    
    // Delete uploaded images from bucket
    for (const img of selectedImages) {
      if (img.uploadedId) {
        try {
          await axios.delete(`${API}/journal/uploaded-image/${img.uploadedId}`);
        } catch (err) {
          console.error('Failed to delete uploaded image:', err);
        }
      }
      URL.revokeObjectURL(img.preview);
    }
    
    // Clear local state
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setContent('');
    setAudioBlob(null);
    setAudioUrl(null);
    setUploadedAudio(null);
    setRecordingTime(0);
    setSelectedImages([]);
    setCustomDate(null);
    localStorage.removeItem('noyo_journal_draft');
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if it's an audio file (handle ALL phone audio types)
    const validAudioTypes = [
      'audio/wav', 'audio/wave', 'audio/x-wav',
      'audio/mp3', 'audio/mpeg', 'audio/mp4', 'audio/m4a', 'audio/x-m4a',
      'audio/webm', 'audio/ogg', 'audio/flac', 'audio/aac', 'audio/x-aac',
      'audio/amr', 'audio/3gpp', 'audio/3gpp2', 'audio/aiff', 'audio/x-aiff',
      'audio/caf', 'audio/x-caf', 'video/3gpp', 'video/mp4' // Some phones save voice memos as video
    ];
    
    const isAudioFile = file.type.startsWith('audio/') || 
                        file.type.startsWith('video/3gpp') ||
                        (file.type === 'video/mp4' && /\.(m4a|aac)$/i.test(file.name)) ||
                        validAudioTypes.includes(file.type.toLowerCase()) ||
                        /\.(wav|mp3|m4a|mp4|webm|ogg|flac|aac|mpeg|mpga|amr|3gp|3gpp|aiff|caf|opus)$/i.test(file.name);
    
    if (!isAudioFile) {
      setDialogConfig({ open: true, title: 'Invalid File', message: 'Please upload an audio file (WAV, MP3, M4A, AAC, etc.)', type: 'alert' });
      return;
    }

    // Check file size (max 25MB for Whisper)
    if (file.size > 25 * 1024 * 1024) {
      setDialogConfig({ open: true, title: 'File Too Large', message: 'File size must be less than 25MB', type: 'alert' });
      return;
    }

    // Discard any existing recording
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }

    // Create blob and URL from uploaded file
    setAudioBlob(file);
    const url = URL.createObjectURL(file);
    setAudioUrl(url);
    
    // Get audio duration when loaded
    const tempAudio = new Audio(url);
    tempAudio.onloadedmetadata = () => {
      setRecordingTime(Math.round(tempAudio.duration) || 0);
    };
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Auto-upload the file to backend with progress
    setIsUploadingAudio(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      // Use original filename with extension for proper format detection
      formData.append('audio', file, file.name);
      const response = await axios.post(`${API}/journal/upload-audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      const { entry_id, audio_filename } = response.data;
      const duration = tempAudio.duration ? Math.round(tempAudio.duration) : 0;
      setUploadedAudio({ entry_id, audio_filename, duration });
      setRecordingSaved(true);
      setTimeout(() => setRecordingSaved(false), 2000);
    } catch (error) {
      console.error('Audio upload error:', error);
      // Show specific error message from backend if available
      const errorMessage = error.response?.data?.detail || 'Failed to upload audio to cloud. Please try again.';
      setDialogConfig({ 
        open: true, 
        title: 'Audio Upload Failed', 
        message: errorMessage, 
        type: 'alert' 
      });
    } finally {
      setIsUploadingAudio(false);
      setUploadProgress(0);
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob) return;
    
    setIsTranscribing(true);
    setTranscriptionProgress(0);
    
    // Simulate progress for better UX (transcription doesn't report progress)
    const progressInterval = setInterval(() => {
      setTranscriptionProgress(prev => {
        if (prev >= 90) return prev; // Cap at 90% until complete
        return prev + Math.random() * 15;
      });
    }, 500);
    
    try {
      const formData = new FormData();
      // Use original filename if available for proper format detection
      const filename = audioBlob.name || 'recording.webm';
      formData.append('audio', audioBlob, filename);
      const response = await axios.post(`${API}/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTranscriptionProgress(100);
      if (response.data.success && response.data.text) {
        setContent(prev => prev.trim() ? prev + '\n\n' + response.data.text : response.data.text);
      }
    } catch (error) {
      console.error('Transcription error:', error);
      setDialogConfig({ open: true, title: 'Error', message: 'Failed to transcribe audio. Please try again.', type: 'alert' });
    } finally {
      clearInterval(progressInterval);
      setIsTranscribing(false);
      setTranscriptionProgress(0);
    }
  };

  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    
    const remaining = 10 - selectedImages.length;
    const toAdd = files.slice(0, remaining);
    
    const newImages = toAdd.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setSelectedImages(prev => [...prev, ...newImages]);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const removeSelectedImage = (index) => {
    setSelectedImages(prev => {
      const removed = prev[index];
      URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadImages = async () => {
    setIsUploadingImages(true);
    setImageUploadProgress(0);
    const filenames = [];
    const imageRecords = [];
    const totalImages = selectedImages.length;
    
    for (let i = 0; i < selectedImages.length; i++) {
      const img = selectedImages[i];
      const formData = new FormData();
      formData.append('file', img.file);
      const res = await axios.post(`${API}/journal/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          // Calculate overall progress across all images
          const imageProgress = (progressEvent.loaded / progressEvent.total);
          const overallProgress = Math.round(((i + imageProgress) / totalImages) * 100);
          setImageUploadProgress(overallProgress);
        }
      });
      filenames.push(res.data.filename);
      if (res.data.image) {
        imageRecords.push(res.data.image);
      }
      // Update progress for completed image
      setImageUploadProgress(Math.round(((i + 1) / totalImages) * 100));
    }
    
    setIsUploadingImages(false);
    setImageUploadProgress(0);
    return { filenames, imageRecords };
  };

  const handleGuestUpgraded = (updatedUser) => {
    setShowGuestSignup(false);
    // After upgrade, auto-save the pending entry
    handleSave(true);
  };

  const handleSave = async (skipConfirm = false) => {
    // Allow saving if there's content OR audio OR images
    if (!content.trim() && !audioBlob && selectedImages.length === 0) return;

    // Guest users must sign up before saving
    if (user?.auth_type === 'guest' && !skipConfirm) {
      setShowGuestSignup(true);
      return;
    }

    // Prompt if saving with only images (no text, no audio)
    if (!skipConfirm && !content.trim() && !audioBlob && selectedImages.length > 0) {
      setDialogConfig({
        open: true,
        title: 'Save Without Text?',
        message: 'This entry has no text or audio. Save with images only?',
        type: 'confirm',
        onConfirm: () => { setDialogConfig({ open: false }); handleSave(true); },
        onCancel: () => setDialogConfig({ open: false })
      });
      return;
    }
    
    setIsLoading(true);
    try {
      // Upload images first
      let imageFilenames = [];
      let imageRecords = [];
      if (selectedImages.length > 0) {
        const uploaded = await uploadImages();
        imageFilenames = uploaded.filenames;
        imageRecords = uploaded.imageRecords;
      }
      
      if (uploadedAudio) {
        // Audio was already uploaded — use the new fast save endpoint
        const isLong = uploadedAudio.duration >= 300;
        
        if (isLong && !content.trim()) {
          // Long recording with no text: save immediately, transcribe in background
          await axios.post(`${API}/journal/save-audio-entry`, {
            entry_id: uploadedAudio.entry_id || uploadedAudio.audio_id,
            audio_filename: uploadedAudio.audio_filename,
            object_path: uploadedAudio.object_path,  // New: full GCS path for direct uploads
            content: '',
            image_filenames: imageFilenames,
            images: imageRecords
          });
        } else {
          // Short recording (text already in box from auto-transcribe) or user typed text
          await axios.post(`${API}/journal/save-audio-entry`, {
            entry_id: uploadedAudio.entry_id || uploadedAudio.audio_id,
            audio_filename: uploadedAudio.audio_filename,
            object_path: uploadedAudio.object_path,  // New: full GCS path for direct uploads
            content: content,
            image_filenames: imageFilenames,
            images: imageRecords
          });
        }
      } else if (audioBlob) {
        // Fallback: audio blob exists but wasn't pre-uploaded (e.g. file upload)
        let audioBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(audioBlob);
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
        });
        await axios.post(`${API}/journal/with-audio`, { 
          content,
          audio_data: audioBase64,
          skip_transcription: !content.trim(),
          image_filenames: imageFilenames,
          images: imageRecords
        });
      } else {
        const journalRes = await axios.post(`${API}/journal`, { content, image_filenames: imageFilenames, images: imageRecords, custom_date: customDate ? customDate.toISOString() : null });
        // Show extraction toast if stories were extracted
        if (journalRes.data?._extraction?.stories_extracted > 0) {
          const ext = journalRes.data._extraction;
          const folderNames = (ext.new_folder_suggestions || []).map(f => f.name);
          // Get all folders to find IDs for the suggested folder names
          try {
            const foldersRes = await axios.get(`${API}/folders`);
            const allFolders = foldersRes.data?.folders || foldersRes.data || [];
            const foldersList = allFolders.filter(f => folderNames.includes(f.name)).map(f => ({ name: f.name, id: f.id }));
            // Also include existing folders the stories were sorted into
            if (foldersList.length === 0 && ext.stories_extracted > 0) {
              // Stories went to existing folders — re-fetch to find which
              const expRes = await axios.get(`${API}/experiences`);
              const recentExps = expRes.data.filter(e => e.source_entry_id === journalRes.data.id);
              const uniqueFolders = [...new Set(recentExps.map(e => e.topic_folder))];
              const matchedFolders = allFolders.filter(f => uniqueFolders.includes(f.name)).map(f => ({ name: f.name, id: f.id }));
              setExtractedFoldersToast({ count: ext.stories_extracted, folders: matchedFolders });
            } else {
              setExtractedFoldersToast({ count: ext.stories_extracted, folders: foldersList });
            }
            setTimeout(() => setExtractedFoldersToast(null), 8000);
          } catch { /* folders fetch failed — still show count */
            setExtractedFoldersToast({ count: ext.stories_extracted, folders: [] });
            setTimeout(() => setExtractedFoldersToast(null), 8000);
          }
        }
      }
      
      setContent('');
      localStorage.removeItem('noyo_journal_draft');
      setCustomDate(null);
      // Clear recording state WITHOUT deleting the audio (it's now part of the saved entry)
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      setAudioBlob(null);
      setAudioUrl(null);
      setUploadedAudio(null);
      setRecordingTime(0);
      selectedImages.forEach(img => URL.revokeObjectURL(img.preview));
      setSelectedImages([]);
      await fetchEntries();
      // Scroll to the new entry
      setTimeout(() => {
        if (newEntryRef.current) {
          newEntryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    } catch (error) {
      console.error('Error saving entry:', error);
      setDialogConfig({ open: true, title: 'Save Failed', message: 'Failed to save entry. Please try again.', type: 'alert' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTranscribeEntry = async (entryId) => {
    setTranscribingId(entryId);
    setTranscribeProgress(0);
    
    // Simulate progress since Groq is fast
    transcribeIntervalRef.current = setInterval(() => {
      setTranscribeProgress(prev => prev >= 90 ? prev : prev + Math.random() * 20);
    }, 300);
    
    try {
      const response = await axios.post(`${API}/journal/${entryId}/transcribe`);
      setTranscribeProgress(100);
      if (response.data.success) {
        const storiesExtracted = response.data.stories_extracted || 0;
        const foldersExtracted = response.data.folders || [];
        if (storiesExtracted > 0 && foldersExtracted.length > 0) {
          // Show compact toast with folder links
          setExtractedFoldersToast({ count: storiesExtracted, folders: foldersExtracted });
          setTimeout(() => setExtractedFoldersToast(null), 8000);
        } else if (storiesExtracted > 0) {
          setDialogConfig({ open: true, title: 'Done', message: `Transcription complete! ${storiesExtracted} ${storiesExtracted === 1 ? 'story was' : 'stories were'} extracted.`, type: 'alert' });
        } else {
          setDialogConfig({ open: true, title: 'Done', message: 'Transcription complete!', type: 'alert' });
        }
        await fetchEntries();
      }
    } catch (error) {
      console.error('Error transcribing entry:', error);
      setDialogConfig({ open: true, title: 'Error', message: 'Failed to transcribe. Please try again.', type: 'alert' });
    } finally {
      clearInterval(transcribeIntervalRef.current);
      setTranscribingId(null);
      setTranscribeProgress(0);
    }
  };

  const getEntryFolders = (entryId) => {
    return experiences.filter(exp => exp.source_entry_id === entryId).map(exp => exp.topic_folder);
  };

  const handleAssignToFolder = async (entryId, folderName) => {
    setAssigningId(entryId + folderName);
    try {
      await axios.post(`${API}/journal/${entryId}/assign-folder`, { folder_name: folderName });
      await fetchEntries();
    } catch (error) {
      console.error('Error assigning to folder:', error);
    } finally {
      setAssigningId(null);
    }
  };

  const handleRemoveFromFolder = async (expId, entryId) => {
    setAssigningId(entryId + 'removing');
    try {
      await axios.delete(`${API}/experiences/${expId}`);
      await fetchEntries();
    } catch (error) {
      console.error('Error removing from folder:', error);
    } finally {
      setAssigningId(null);
    }
  };

  const handleCreateAndAssignFolder = async (folderName, entryId) => {
    try {
      await axios.post(`${API}/folders`, { name: folderName });
      await axios.post(`${API}/journal/${entryId}/assign-folder`, { folder_name: folderName });
      await fetchEntries();
    } catch (error) {
      console.error('Error creating folder and assigning:', error);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditContent(entry.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/journal/${id}`, { content: editContent });
      setEditingId(null);
      setEditContent('');
      await fetchEntries();
    } catch (error) {
      console.error('Error updating entry:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/journal/${id}`);
      setDeleteConfirm(null);
      await fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
    }
  };

  return (
    <motion.div
      variants={getPageVariants(direction)}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="min-h-screen pt-20 md:pt-24 pb-12 relative"
      data-testid="journal-page"
    >
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        {/* Page Title */}
        <div className="mb-4 md:mb-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium tracking-tight text-foreground mb-1">Journal</h1>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">Record a story, memory, or journal entry</p>
        </div>

        <div className="mb-6 md:mb-8">
          <div className="bg-card backdrop-blur-sm border border-border rounded-2xl p-4 md:p-6 shadow-lg" data-testid="journal-entry-form">
            {/* Plant streak icon - top right */}
            <div className="flex justify-end -mt-1 -mr-1 mb-1">
              <button
                onClick={() => setShowStreakPanel(!showStreakPanel)}
                className={`p-1.5 rounded-lg transition-all ${showStreakPanel ? 'bg-green-500/20 text-green-400' : 'text-muted-foreground/40 hover:text-green-400 hover:bg-green-500/10'}`}
                data-testid="streak-panel-toggle"
                title="View growth streak"
              >
                <Leaf className="w-5 h-5" />
              </button>
            </div>

            {/* Collapsible streak panel */}
            {showStreakPanel && (
              <div
                className="overflow-hidden transition-all duration-300"
                data-testid="streak-inline-panel"
              >
                {streakData ? (
                <div className="pb-5 mb-3 border-b border-border">
                  {/* Plant + Number row */}
                  <div className="flex items-end justify-center gap-1 mb-1">
                    {/* Plant SVG */}
                    <div className="w-16 h-20 flex-shrink-0 relative">
                      <svg viewBox="0 0 60 80" className="w-full h-full plant-animated">
                        {/* Ground line */}
                        <rect x="8" y="68" width="44" height="3" rx="1.5" fill="hsl(25, 30%, 20%)" />

                        {streakData.current_streak === 0 && (
                          <>
                            {/* Tiny nub barely poking out */}
                            <path d="M30 68 C30 66, 30 65, 30 64" stroke="var(--plant-sprout)" strokeWidth="2" fill="none" strokeLinecap="round" />
                            <ellipse cx="30" cy="63" rx="2.5" ry="2" fill="var(--plant-sprout)" opacity="0.7" />
                          </>
                        )}

                        {streakData.current_streak >= 1 && streakData.current_streak < 3 && (
                          <>
                            {/* Small sprout - just popping out */}
                            <path d="M30 68 C30 62, 30 58, 30 52" stroke="var(--plant-leaf-light)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                            <path d="M30 58 C25 52, 18 54, 21 58 C19 53, 25 50, 30 58" fill="var(--plant-leaf-light)" />
                            <path d="M30 58 C35 52, 42 54, 39 58 C41 53, 35 50, 30 58" fill="var(--plant-leaf-dark)" />
                          </>
                        )}

                        {streakData.current_streak >= 3 && streakData.current_streak < 7 && (
                          <>
                            {/* Taller plant with leaf pairs */}
                            <path d="M30 68 C30 55, 29 42, 30 30" stroke="var(--plant-stem)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                            <path d="M30 58 C23 50, 14 53, 18 58 C15 51, 23 46, 30 58" fill="var(--plant-leaf-light)" />
                            <path d="M30 58 C37 50, 46 53, 42 58 C45 51, 37 46, 30 58" fill="var(--plant-leaf-dark)" />
                            <path d="M30 44 C22 36, 12 39, 16 44 C13 37, 22 32, 30 44" fill="var(--plant-leaf-accent)" />
                            <path d="M30 44 C38 36, 48 39, 44 44 C47 37, 38 32, 30 44" fill="var(--plant-leaf-light)" />
                          </>
                        )}

                        {streakData.current_streak >= 7 && (
                          <>
                            {/* Full plant with bloom */}
                            <path d="M30 68 C30 50, 28 35, 30 18" stroke="var(--plant-stem)" strokeWidth="3" fill="none" strokeLinecap="round" />
                            <path d="M30 55 C21 46, 10 49, 15 55 C11 47, 21 42, 30 55" fill="var(--plant-leaf-light)" />
                            <path d="M30 55 C39 46, 50 49, 45 55 C49 47, 39 42, 30 55" fill="var(--plant-leaf-dark)" />
                            <path d="M30 40 C20 31, 8 34, 13 40 C9 32, 20 27, 30 40" fill="var(--plant-leaf-accent)" />
                            <path d="M30 40 C40 31, 52 34, 47 40 C51 32, 40 27, 30 40" fill="var(--plant-leaf-light)" />
                            <circle cx="30" cy="14" r="6" fill="#fb7185" />
                            <circle cx="25" cy="12" r="3.5" fill="#f472b6" opacity="0.8" />
                            <circle cx="35" cy="12" r="3.5" fill="#f472b6" opacity="0.8" />
                            <circle cx="30" cy="8" r="3.5" fill="#f472b6" opacity="0.8" />
                            <circle cx="30" cy="14" r="2.5" fill="#fbbf24" />
                          </>
                        )}
                      </svg>
                    </div>

                    {/* Big bold streak number */}
                    <div>
                      <span className="text-6xl font-black text-foreground leading-none tracking-tighter">{streakData.current_streak}</span>
                    </div>
                  </div>
                  <p className="text-center text-sm font-bold text-muted-foreground uppercase tracking-widest mb-4">Day Streak</p>

                  {/* Weekly calendar - bigger, bolder */}
                  {streakData.week_days && streakData.week_days.length > 0 && (
                    <div className="flex justify-center gap-2" data-testid="streak-inline-calendar">
                      {streakData.week_days.map((day) => (
                        <div key={day.label} className="flex flex-col items-center gap-1.5">
                          <span className="text-[11px] font-bold text-muted-foreground">{day.label}</span>
                          {day.active ? (
                            <div className="w-8 h-8 rounded-xl bg-emerald-600/25 dark:bg-green-500/25 flex items-center justify-center ring-2 ring-emerald-600/50 dark:ring-green-400/40">
                              <Check className="w-4 h-4 text-emerald-700 dark:text-green-400" strokeWidth={3} />
                            </div>
                          ) : day.is_today ? (
                            <div className="w-8 h-8 rounded-xl border-2 border-dashed border-emerald-600/60 dark:border-green-400/50 flex items-center justify-center">
                              <span className="w-2.5 h-2.5 rounded-full bg-emerald-600/50 dark:bg-green-400/40 animate-pulse" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-xl bg-muted/15 flex items-center justify-center">
                              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/10" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Bold motivational text */}
                  <p className="text-sm font-bold text-muted-foreground text-center mt-4">
                    {streakData.current_streak >= 7 ? (
                      <>Your plant is <span className="text-emerald-700 dark:text-green-400">flourishing</span>!</>
                    ) : streakData.current_streak >= 3 ? (
                      <>Your plant is <span className="text-emerald-700 dark:text-green-400">growing</span>, keep it up!</>
                    ) : streakData.current_streak >= 1 ? (
                      <>Your plant is <span className="text-emerald-700 dark:text-green-400">sprouting</span>!</>
                    ) : (
                      <>Add a story or journal to <span className="text-emerald-700 dark:text-green-400">water your plant</span></>
                    )}
                  </p>
                </div>
                ) : (
                  <div className="pb-4 mb-3 border-b border-border flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                )}
              </div>
            )}

            <div className="relative">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onFocus={() => setIsTextareaFocused(true)}
                onBlur={() => setIsTextareaFocused(false)}
                className={`w-full bg-transparent border-none leading-loose focus:outline-none resize-none font-serif min-h-[280px] md:min-h-[340px] caret-primary transition-all duration-200 ${
                  content || isTextareaFocused 
                    ? 'text-lg sm:text-xl md:text-2xl' 
                    : 'text-xl sm:text-2xl md:text-3xl lg:text-4xl'
                }`}
                data-testid="journal-textarea"
              />
              {!content && !isTextareaFocused && (
                <div className="absolute inset-0 pointer-events-none select-none z-20 p-px flex items-start">
                  <span className="inline-block w-0.5 h-8 sm:h-10 md:h-12 bg-primary mr-1 animate-blink"></span>
                  <span className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-serif text-foreground placeholder-breathe">What's on your mind today?</span>
                </div>
              )}
            </div>

            {/* Icons just above the save row */}
            {!audioBlob && !isRecording && (
              <div className="flex items-center justify-end space-x-2 -mb-px">
                <button
                  onClick={() => imageInputRef.current?.click()}
                  disabled={selectedImages.length >= 10}
                  className="p-2 rounded-lg hover:bg-secondary/50 transition-all disabled:opacity-40 disabled:cursor-not-allowed text-primary dark:text-white"
                  data-testid="attach-images-button"
                  title="Attach Images"
                >
                  <ImagePlus className="w-7 h-7" />
                </button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                  data-testid="image-file-input"
                />
                
                <div className="relative" ref={audioDropdownRef}>
                  <button
                    onClick={() => setAudioDropdownOpen(!audioDropdownOpen)}
                    className="p-2 rounded-lg hover:bg-secondary/50 transition-all text-primary dark:text-white"
                    data-testid="audio-options-button"
                    title="Use Audio"
                  >
                    <Mic className="w-7 h-7" />
                  </button>
                  
                  {audioDropdownOpen && (
                    <div className="absolute bottom-full right-0 mb-2 bg-card-solid border border-border rounded-xl shadow-xl z-50 min-w-[180px]">
                      <button
                        onClick={() => { startRecording(); setAudioDropdownOpen(false); }}
                        className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-foreground hover:bg-secondary/50 transition-colors"
                        data-testid="start-recording-button"
                      >
                        <Mic className="w-4 h-4 text-primary" />
                        <span>Record Audio</span>
                      </button>
                      <button
                        onClick={() => { fileInputRef.current?.click(); setAudioDropdownOpen(false); }}
                        className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-foreground hover:bg-secondary/50 transition-colors border-t border-border"
                        data-testid="upload-audio-button"
                      >
                        <Upload className="w-4 h-4 text-primary" />
                        <span>Upload Audio</span>
                      </button>
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".wav,.mp3,.m4a,.mp4,.webm,.ogg,.flac,.aac,.mpeg,.amr,.3gp,.3gpp,.aiff,.caf,.opus,audio/*,video/3gpp,video/mp4"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="audio-file-input"
                  />
                </div>
              </div>
            )}
            
            <div className="border-t border-border pt-1.5">
              {audioUrl && (
                <audio 
                  ref={audioRef} 
                  src={audioUrl} 
                  onEnded={() => setIsPlaying(false)}
                  className="hidden"
                />
              )}
              
              {isRecording && (
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    {isPaused ? (
                      <span className="w-3 h-3 bg-orange-500 rounded-full" />
                    ) : (
                      <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                    )}
                    <span className={`text-sm font-medium ${isPaused ? 'text-orange-500' : 'text-red-500'}`}>
                      {isPaused ? 'Paused' : 'Recording'} {formatTime(recordingTime)}
                    </span>
                  </div>
                  
                  {/* Pause/Resume Button */}
                  {isPaused ? (
                    <button
                      onClick={resumeRecording}
                      className="flex items-center space-x-2 bg-primary text-white rounded-full px-4 py-2 text-sm hover:bg-primary/80 transition-colors"
                      data-testid="resume-recording-button"
                    >
                      <Play className="w-4 h-4" />
                      <span>Resume</span>
                    </button>
                  ) : (
                    <button
                      onClick={pauseRecording}
                      className="flex items-center space-x-2 bg-orange-500 text-white rounded-full px-4 py-2 text-sm hover:bg-orange-600 transition-colors"
                      data-testid="pause-recording-button"
                    >
                      <Pause className="w-4 h-4" />
                      <span>Pause</span>
                    </button>
                  )}
                  
                  {/* Stop Button */}
                  <button
                    onClick={stopRecording}
                    className="flex items-center space-x-2 bg-red-500 text-white rounded-full px-4 py-2 text-sm hover:bg-red-600 transition-colors"
                    data-testid="stop-recording-button"
                  >
                    <Square className="w-4 h-4" />
                    <span>Stop</span>
                  </button>
                </div>
              )}
              
              {audioBlob && !isRecording && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center space-x-3 bg-secondary/50 rounded-full px-4 py-2">
                    <button
                      onClick={togglePlayback}
                      className="text-primary hover:text-[#7A9186] transition-colors"
                      data-testid="playback-button"
                      title={isPlaying ? "Pause" : "Play"}
                    >
                      {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                    </button>
                    <Volume2 className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{formatTime(recordingTime)}</span>
                  </div>
                  
                  {isUploadingAudio ? (
                    <ProgressBar 
                      progress={uploadProgress} 
                      label="Saving to cloud" 
                      variant="primary"
                      data-testid="audio-uploading-status"
                    />
                  ) : isTranscribing ? (
                    <ProgressBar 
                      progress={transcriptionProgress} 
                      label="Transcribing" 
                      variant="accent"
                      data-testid="audio-transcribing-status"
                    />
                  ) : (
                    <div className="flex items-center space-x-2">
                      {!uploadedAudio && (
                        <button
                          onClick={transcribeAudio}
                          disabled={isTranscribing}
                          className="flex items-center space-x-1 bg-accent/10 text-accent hover:bg-accent/20 rounded-full px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50"
                          data-testid="transcribe-button"
                        >
                          <Sparkles className="w-4 h-4" />
                          <span>Transcribe</span>
                        </button>
                      )}
                      <button
                        onClick={discardRecording}
                        className="flex items-center space-x-1 text-muted-foreground hover:text-red-500 transition-colors text-sm"
                        data-testid="discard-recording-button"
                      >
                        <X className="w-4 h-4" />
                        <span>Discard</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Selected Image Thumbnails */}
            {selectedImages.length > 0 && (
              <div className="flex items-center gap-3 mt-4 flex-wrap" data-testid="selected-images-preview">
                {selectedImages.map((img, idx) => (
                  <div key={img.preview || `img-${idx}`} className="relative group w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border border-border shadow-sm cursor-pointer" onClick={() => setLightboxImage({ imageUrl: img.preview, downloadUrl: null })}>
                    <img src={img.preview} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={(e) => { e.stopPropagation(); removeSelectedImage(idx); }}
                      className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-image-${idx}`}
                      title="Remove image"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {selectedImages.length < 10 && (
                  <button
                    onClick={() => imageInputRef.current?.click()}
                    className="w-16 h-16 md:w-20 md:h-20 rounded-xl border-2 border-dashed border-border hover:border-primary/50 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                    data-testid="add-more-images-button"
                    title="Add more images"
                  >
                    <Paperclip className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
            
            {/* Image Upload Progress */}
            {isUploadingImages && (
              <div className="mt-3">
                <ProgressBar 
                  progress={imageUploadProgress} 
                  label="Uploading images" 
                  variant="primary"
                  data-testid="image-uploading-status"
                />
              </div>
            )}
            
            <div className="flex flex-wrap items-center justify-end gap-2 mt-3 md:mt-4">
              {audioBlob && !isUploadingAudio && !isTranscribing && uploadedAudio && (
                <span className="text-xs text-primary flex items-center space-x-1 mr-auto">
                  <Check className="w-3 h-3" />
                  <span>{uploadedAudio.duration >= 300 ? 'Audio saved — will transcribe in background on save' : 'Audio saved & transcribed'}</span>
                </span>
              )}
              {audioBlob && !uploadedAudio && !isUploadingAudio && (
                <span className="text-xs text-muted-foreground flex items-center space-x-1 mr-auto">
                  <Volume2 className="w-3 h-3" />
                  <span>Audio will be saved with entry</span>
                </span>
              )}
              {draftSaved && (
                <span className="text-xs text-muted-foreground animate-pulse mr-auto">Draft saved</span>
              )}
              {recordingSaved && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-xs text-green-600 dark:text-green-400 font-medium mr-auto flex items-center gap-1"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Audio saved to cloud
                </motion.span>
              )}
              <div className="inline-flex items-center gap-2 flex-shrink-0">
                {/* Discard Entry Button - only show when there's content */}
                {(content.trim() || audioBlob || selectedImages.length > 0 || uploadedAudio) && (
                  <button
                    onClick={handleDiscardEntry}
                    className="flex items-center space-x-1.5 text-sm md:text-base rounded-full px-3 md:px-4 py-2.5 md:py-3 transition-all text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 font-medium"
                    data-testid="discard-entry-button"
                    title="Discard entry"
                  >
                    <Trash2 className="w-4 h-4 md:w-5 md:h-5" />
                    <span className="hidden sm:inline">Discard</span>
                  </button>
                )}
                <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                  <PopoverTrigger asChild>
                    <button
                      className={`flex items-center space-x-1.5 text-sm md:text-base rounded-full px-4 md:px-6 py-2.5 md:py-3 transition-all text-primary font-medium border ${
                        customDate
                          ? 'border-transparent'
                          : showDatePicker
                            ? 'border-primary bg-primary/5'
                            : 'border-transparent hover:border-primary'
                      }`}
                      data-testid="change-date-button"
                      title="Change entry date"
                    >
                      <CalendarDays className="w-4 h-4 md:w-5 md:h-5" />
                      <span>{(customDate || new Date()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-background/80 backdrop-blur-xl border border-border/50 shadow-xl" align="end">
                    <ScrollDatePicker
                      date={customDate || new Date()}
                      onDateChange={(d) => setCustomDate(d)}
                      onClose={() => setShowDatePicker(false)}
                    />
                  </PopoverContent>
                </Popover>
                <button
                  onClick={handleSave}
                  disabled={isLoading || isUploadingImages || (!content.trim() && !audioBlob && selectedImages.length === 0)}
                  className="bg-primary text-white hover:shadow-lg hover:brightness-95 rounded-full px-5 md:px-8 py-2.5 md:py-3 text-sm md:text-base font-medium transition-all duration-300 shadow-sm active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center space-x-2"
                  data-testid="save-journal-button"
                >
                  {isLoading || isUploadingImages ? (
                    <>
                      <Sparkles className="w-4 h-4 md:w-5 md:h-5 animate-pulse" />
                      <span>{isUploadingImages ? 'Uploading...' : 'Processing...'}</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 md:w-5 md:h-5" />
                      <span>Save</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Entries Section - Always visible */}
        <div className="space-y-4 md:space-y-6" data-testid="journal-entries-list">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="text-xl md:text-2xl font-serif font-medium text-foreground">Recent Entries</h2>
            {entries.length > 0 && (
              <button
                onClick={() => navigate('/timeline')}
                className="flex items-center space-x-1 text-sm text-primary hover:text-[#7A9186] transition-colors"
                data-testid="see-more-entries-button"
              >
                <span>View all entries</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {isLoadingEntries ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-card border border-border rounded-2xl p-5">
                  <div className="skeleton h-3 w-32 mb-4" />
                  <div className="skeleton h-4 w-full mb-2" />
                  <div className="skeleton h-4 w-3/4 mb-4" />
                  <div className="skeleton h-8 w-48" />
                </div>
              ))}
            </div>
          ) : entries.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl p-8 md:p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-primary/60" />
              </div>
              <h3 className="text-lg font-serif font-medium text-foreground mb-2">No entries yet</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Start capturing your thoughts above. Your journal entries will appear here.
              </p>
            </div>
          ) : (
            <>
              {entries.slice(0, 10).map((entry, index) => (
              <motion.div
                key={entry.id}
                ref={index === 0 ? newEntryRef : null}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-card border rounded-2xl p-4 md:p-6 transition-all duration-300 hover:shadow-lg ${
                  highlightedEntry === entry.id 
                    ? 'border-primary shadow-lg ring-2 ring-primary/20' 
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid={`journal-entry-${entry.id}`}
                data-entry-id={entry.id}
              >
                {editingId === entry.id ? (
                  <div>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="w-full bg-transparent border border-input rounded-xl p-3 md:p-4 text-sm md:text-base font-serif leading-relaxed focus:outline-none focus:border-primary min-h-[120px] md:min-h-[150px]"
                      data-testid={`edit-textarea-${entry.id}`}
                    />
                    <div className="flex justify-end space-x-2 mt-3 md:mt-4">
                      <button
                        onClick={cancelEdit}
                        className="bg-transparent text-foreground hover:bg-secondary rounded-full px-4 md:px-6 py-2 text-sm md:text-base transition-colors flex items-center space-x-1 md:space-x-2"
                        data-testid={`cancel-edit-button-${entry.id}`}
                      >
                        <X className="w-4 h-4" />
                        <span>Cancel</span>
                      </button>
                      <button
                        onClick={() => saveEdit(entry.id)}
                        className="bg-primary text-white hover:bg-primary/80 rounded-full px-4 md:px-6 py-2 text-sm md:text-base transition-colors flex items-center space-x-1 md:space-x-2"
                        data-testid={`save-edit-button-${entry.id}`}
                      >
                        <Save className="w-4 h-4" />
                        <span>Save</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Header: Date box + Edit button (timeline style) */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex flex-col items-center justify-center w-12 h-12 rounded-xl bg-primary/10 border border-primary/20">
                          <span className="text-lg font-serif font-bold text-primary">{new Date(entry.timestamp).getDate()}</span>
                          <span className="text-[8px] text-primary uppercase tracking-wider">{new Date(entry.timestamp).toLocaleDateString('en-US', { weekday: 'short' })}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{new Date(entry.timestamp).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                          <p className="text-xs text-muted-foreground">{new Date(entry.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/entry/${entry.id}/edit`)}
                        className="text-sm text-primary hover:text-[#7A9186] transition-colors flex items-center space-x-1"
                        data-testid={`edit-journal-button-${entry.id}`}
                      >
                        <span>Edit</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Content */}
                    <div className="mt-3">
                    {entry.content === "Voice Recording" ? (
                      <div className="flex items-center space-x-2 text-sm md:text-base text-foreground mb-3" data-testid={`journal-content-${entry.id}`}>
                        <Mic className="w-4 h-4 text-muted-foreground" />
                        <span className="font-serif">Voice Recording</span>
                      </div>
                    ) : (
                      <div data-testid={`journal-content-${entry.id}`} className="mb-3">
                        <p className="text-sm md:text-base leading-relaxed font-serif text-foreground whitespace-pre-wrap">
                          <LinkifyText text={expandedEntries[entry.id] || entry.content.length <= MAX_PREVIEW_LENGTH
                            ? entry.content
                            : `${entry.content.slice(0, MAX_PREVIEW_LENGTH)}...`} />
                        </p>
                        {entry.content.length > MAX_PREVIEW_LENGTH && (
                          <button
                            onClick={() => toggleEntryExpansion(entry.id)}
                            className="mt-2 text-sm text-primary hover:text-[#7A9186] transition-colors font-medium"
                            data-testid={`toggle-entry-${entry.id}`}
                          >
                            {expandedEntries[entry.id] ? 'Show less' : 'See more'}
                          </button>
                        )}
                      </div>
                    )}
                    </div>

                    {/* Audio summary for untranscribed */}
                    {entry.has_audio && entry.is_transcribed === false && entry.audio_summary && (
                      <div className="mb-3 p-3 bg-accent/5 border border-accent/20 rounded-xl">
                        <p className="text-sm text-muted-foreground italic" data-testid={`audio-summary-${entry.id}`}>
                          {entry.audio_summary}
                        </p>
                      </div>
                    )}
                    {entry.transcription_status === 'processing' && (
                      <div className="mb-3 p-3 bg-primary/5 border border-primary/20 rounded-xl flex items-center space-x-2" data-testid={`transcription-processing-${entry.id}`}>
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                        <p className="text-sm text-primary font-medium">Transcribing in background...</p>
                      </div>
                    )}
                    {entry.transcription_status === 'failed' && (
                      <div className="mb-3 p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center justify-between" data-testid={`transcription-failed-${entry.id}`}>
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                          <p className="text-sm text-red-500 font-medium">Transcription failed</p>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/journal/${entry.id}/retry-transcription`);
                              fetchEntries();
                            } catch (err) {
                              console.error('Retry failed:', err);
                            }
                          }}
                          className="px-3 py-1 text-xs font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                          data-testid={`retry-transcription-${entry.id}`}
                        >
                          Retry
                        </button>
                      </div>
                    )}

                    {/* Audio Player */}
                    {entry.has_audio && !entry.audio_missing && (
                      <div className="mb-3 p-3 bg-secondary/30 rounded-xl">
                        <AudioErrorBoundary>
                          <AudioPlayer entryId={entry.id} />
                        </AudioErrorBoundary>
                      </div>
                    )}
                    {entry.has_audio && entry.audio_missing && (
                      <div className="mb-3 p-3 bg-secondary/30 rounded-xl">
                        <p className="text-xs text-muted-foreground">Audio file no longer available</p>
                      </div>
                    )}
                    
                    {/* Attached Images */}
                    {((entry.images && entry.images.length > 0) || (entry.image_filenames && entry.image_filenames.length > 0)) && (
                      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid={`entry-images-${entry.id}`}>
                        {(entry.images?.length > 0 ? entry.images : entry.image_filenames).map((_, idx) => {
                          const thumbUrl = getImageUrl(entry, idx, 'thumb');
                          const fullUrl = getImageUrl(entry, idx, 'full');
                          const downloadUrl = getDownloadUrl(entry, idx);
                          const dims = getImageDimensions(entry, idx);
                          if (!thumbUrl) return null;
                          return (
                            <div key={idx} className="relative group cursor-pointer" onClick={() => setLightboxImage({ imageUrl: fullUrl, downloadUrl: downloadUrl })}>
                              <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border border-border shadow-sm hover:shadow-md transition-shadow bg-secondary/30">
                                <img
                                  src={thumbUrl}
                                  alt=""
                                  loading="lazy"
                                  width={dims?.width ? Math.min(80, dims.width) : 80}
                                  height={dims?.height ? Math.min(80, dims.height) : 80}
                                  className="w-full h-full object-cover"
                                  data-testid={`entry-image-${entry.id}-${idx}`}
                                />
                              </div>
                              <a
                                href={downloadUrl || '#'}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute bottom-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Download Original"
                                data-testid={`download-image-${entry.id}-${idx}`}
                              >
                                <Download className="w-3 h-3" />
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Bottom: Delete + Folder only */}
                    <div className="flex items-center justify-between pt-3 border-t border-border">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => setDeleteConfirm(entry.id)}
                          className="hover:text-red-500 transition-colors p-1 text-[#c4a485] dark:text-foreground/70"
                          data-testid={`delete-journal-button-${entry.id}`}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (assignDropdownOpen === entry.id) {
                              setAssignDropdownOpen(null);
                              return;
                            }
                            const currentFolders = new Set(getEntryFolders(entry.id));
                            setInitialSelections(new Set(currentFolders));
                            setPendingSelections(new Set(currentFolders));
                            setPendingNewFolders([]);
                            setInlineFolderName('');
                            setAssignDropdownOpen(entry.id);
                          }}
                          className={`transition-colors hover:opacity-70 ${assignDropdownOpen === entry.id ? 'text-[#b0926a] dark:text-foreground/90' : 'text-[#c4a485] dark:text-foreground/70'}`}
                          data-testid={`assign-folder-button-${entry.id}`}
                          title="Add to Folder"
                        >
                          <FolderPlus className="w-4 h-4" />
                        </button>
                      </div>
                      {entry.has_audio && !entry.is_transcribed && (
                        transcribingId === entry.id ? (
                          <ProgressBar 
                            progress={Math.round(transcribeProgress)} 
                            label="Transcribing" 
                            variant="accent"
                            data-testid={`transcribe-progress-${entry.id}`}
                          />
                        ) : (
                          <button
                            onClick={() => handleTranscribeEntry(entry.id)}
                            className="text-xs bg-accent/10 text-accent hover:bg-accent/20 rounded-full px-3 py-1.5 font-medium transition-colors flex items-center space-x-1 active:scale-95"
                            data-testid={`transcribe-entry-button-${entry.id}`}
                          >
                            <Sparkles className="w-3 h-3" />
                            <span>Transcribe</span>
                          </button>
                        )
                      )}
                    </div>

                    {/* Inline Folder Dropdown - Batch mode */}
                    {assignDropdownOpen === entry.id && (
                      <div className="mt-3 pt-3 border-t border-border" data-testid={`assign-folder-dropdown-${entry.id}`}>
                        <div className="mb-2">
                          <span className="text-xs font-medium text-muted-foreground">Select folders</span>
                        </div>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          <button
                            onClick={() => {
                              setPendingSelections(prev => {
                                const next = new Set(prev);
                                next.has('Unsorted Stories') ? next.delete('Unsorted Stories') : next.add('Unsorted Stories');
                                return next;
                              });
                            }}
                            className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                              pendingSelections.has('Unsorted Stories') ? 'bg-primary/10' : 'hover:bg-secondary/50'
                            }`}
                            data-testid="assign-folder-option-unsorted"
                          >
                            <span className="flex items-center justify-between">
                              <span className={pendingSelections.has('Unsorted Stories') ? 'text-primary font-medium' : 'text-muted-foreground'}>Unsorted Stories</span>
                              {pendingSelections.has('Unsorted Stories') && (
                                <span className="flex items-center space-x-1.5">
                                  <Check className="w-4 h-4 text-primary" />
                                  <XCircle className="w-4 h-4 text-red-400 hover:text-red-500" />
                                </span>
                              )}
                            </span>
                          </button>
                          {folders.map(folder => (
                            <button
                              key={folder.id}
                              onClick={() => {
                                setPendingSelections(prev => {
                                  const next = new Set(prev);
                                  next.has(folder.name) ? next.delete(folder.name) : next.add(folder.name);
                                  return next;
                                });
                              }}
                              className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                                pendingSelections.has(folder.name) ? 'bg-primary/10' : 'hover:bg-secondary/50'
                              }`}
                              data-testid={`assign-folder-option-${folder.id}`}
                            >
                              <span className="flex items-center justify-between">
                                <span className={pendingSelections.has(folder.name) ? 'text-primary font-medium' : ''}>{folder.name}</span>
                                {pendingSelections.has(folder.name) && (
                                  <span className="flex items-center space-x-1.5">
                                    <Check className="w-4 h-4 text-primary" />
                                    <XCircle className="w-4 h-4 text-red-400 hover:text-red-500" />
                                  </span>
                                )}
                              </span>
                            </button>
                          ))}
                          {pendingNewFolders.map(name => (
                            <button
                              key={`new-${name}`}
                              onClick={() => {
                                setPendingSelections(prev => {
                                  const next = new Set(prev);
                                  if (next.has(name)) {
                                    next.delete(name);
                                    setPendingNewFolders(p => p.filter(n => n !== name));
                                  } else {
                                    next.add(name);
                                  }
                                  return next;
                                });
                              }}
                              className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                                pendingSelections.has(name) ? 'bg-primary/10' : 'hover:bg-secondary/50'
                              }`}
                              data-testid={`assign-folder-option-new-${name}`}
                            >
                              <span className="flex items-center justify-between">
                                <span className={pendingSelections.has(name) ? 'text-primary font-medium' : ''}>{name} <span className="text-xs italic text-muted-foreground">(new)</span></span>
                                {pendingSelections.has(name) && (
                                  <span className="flex items-center space-x-1.5">
                                    <Check className="w-4 h-4 text-primary" />
                                    <XCircle className="w-4 h-4 text-red-400 hover:text-red-500" />
                                  </span>
                                )}
                              </span>
                            </button>
                          ))}
                        </div>
                        <div className="mt-2 pt-2 border-t border-border">
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              const name = inlineFolderName.trim();
                              if (!name) return;
                              if (!pendingNewFolders.includes(name) && !folders.some(f => f.name === name)) {
                                setPendingNewFolders(prev => [...prev, name]);
                              }
                              setPendingSelections(prev => new Set([...prev, name]));
                              setInlineFolderName('');
                            }}
                            className="flex items-center space-x-2"
                          >
                            <input
                              type="text"
                              value={inlineFolderName}
                              onChange={(e) => setInlineFolderName(e.target.value)}
                              placeholder="New folder..."
                              className="flex-1 text-sm bg-secondary/30 border-none rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
                              data-testid="inline-folder-name-input"
                            />
                            <button
                              type="submit"
                              disabled={!inlineFolderName.trim()}
                              className="bg-primary text-primary-foreground px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                              data-testid="inline-create-folder-button"
                            >
                              <PlusCircle className="w-4 h-4" />
                            </button>
                          </form>
                        </div>
                        <div className="mt-3 bg-muted/60 rounded-xl px-3 py-2.5 flex items-center justify-end space-x-2">
                          <button
                            onClick={() => {
                              setAssignDropdownOpen(null);
                              setPendingSelections(new Set());
                              setInitialSelections(new Set());
                              setPendingNewFolders([]);
                              setInlineFolderName('');
                            }}
                            className="px-4 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-lg"
                            data-testid={`folder-cancel-${entry.id}`}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={async () => {
                              setIsSavingFolders(true);
                              try {
                                for (const name of pendingNewFolders) {
                                  try {
                                    const res = await axios.post(`${API}/folders`, { name });
                                    setFolders(prev => [...prev, res.data]);
                                  } catch (err) { console.error(err); }
                                }
                                const toAdd = [...pendingSelections].filter(f => !initialSelections.has(f));
                                const toRemove = [...initialSelections].filter(f => !pendingSelections.has(f));
                                for (const folderName of toAdd) {
                                  await handleAssignToFolder(entry.id, folderName);
                                }
                                for (const folderName of toRemove) {
                                  const matchExp = experiences.find(exp => exp.source_entry_id === entry.id && exp.topic_folder === folderName);
                                  if (matchExp) await handleRemoveFromFolder(matchExp.id, entry.id);
                                }
                              } catch (err) { console.error(err); }
                              setIsSavingFolders(false);
                              setAssignDropdownOpen(null);
                              setPendingSelections(new Set());
                              setInitialSelections(new Set());
                              setPendingNewFolders([]);
                              setInlineFolderName('');
                            }}
                            disabled={isSavingFolders}
                            className="px-4 py-1.5 text-sm bg-foreground text-background font-medium rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
                            data-testid={`folder-done-${entry.id}`}
                          >
                            {isSavingFolders ? 'Saving...' : 'Done'}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {deleteConfirm === entry.id && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                    data-testid="delete-confirmation-modal"
                  >
                    <div className="bg-card-solid rounded-2xl p-6 max-w-md mx-4 shadow-2xl">
                      <h3 className="text-xl font-serif font-medium mb-3">Delete Journal Entry?</h3>
                      <p className="text-muted-foreground mb-6">This will also delete all experiences extracted from this entry. This action cannot be undone.</p>
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="bg-transparent text-foreground hover:bg-secondary rounded-full px-6 py-2 transition-colors"
                          data-testid="cancel-delete-button"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="bg-red-500 text-white hover:bg-red-600 rounded-full px-6 py-2 transition-colors"
                          data-testid="confirm-delete-button"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
            
            {/* See More Button at bottom */}
            {entries.length > 10 && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => navigate('/timeline')}
                className="w-full py-4 text-center text-primary hover:text-[#7A9186] transition-colors border border-border hover:border-primary/50 rounded-2xl flex items-center justify-center space-x-2"
                data-testid="see-more-entries-bottom-button"
              >
                <span>See all {entries.length} entries</span>
                <ChevronRight className="w-4 h-4" />
              </motion.button>
            )}
            </>
          )}
        </div>
      </div>
      
      {/* Image Lightbox */}
      {lightboxImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[200] p-4 overflow-hidden"
          onClick={() => setLightboxImage(null)}
          data-testid="image-lightbox"
          style={{ touchAction: 'none' }}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full w-10 h-10 flex items-center justify-center z-10"
            data-testid="close-lightbox-button"
          >
            <X className="w-6 h-6" />
          </button>
          {lightboxImage.downloadUrl && (
            <a
              href={lightboxImage.downloadUrl}
              onClick={(e) => e.stopPropagation()}
              className="absolute top-4 left-4 text-white/80 hover:text-white bg-black/40 rounded-full w-10 h-10 flex items-center justify-center z-10"
              title="Download"
              data-testid="lightbox-download-button"
            >
              <Download className="w-5 h-5" />
            </a>
          )}
          <img
            src={lightboxImage.imageUrl}
            alt=""
            className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </motion.div>
      )}

      {/* Themed Dialog */}
      <ThemedDialog
        open={dialogConfig.open}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
        onConfirm={dialogConfig.onConfirm || (() => setDialogConfig({ open: false }))}
        onCancel={dialogConfig.onCancel || (() => setDialogConfig({ open: false }))}
      />

      {/* Extracted Folders Toast - Compact mobile-friendly notification */}
      <AnimatePresence>
        {extractedFoldersToast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50"
            data-testid="extracted-folders-toast"
          >
            <div className="bg-card border border-border rounded-xl shadow-lg p-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-accent" />
                  </div>
                  <p className="text-sm font-medium text-foreground">
                    {extractedFoldersToast.count} {extractedFoldersToast.count === 1 ? 'story' : 'stories'} extracted
                  </p>
                </div>
                <button 
                  onClick={() => setExtractedFoldersToast(null)}
                  className="text-muted-foreground hover:text-foreground p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 pl-10">
                {extractedFoldersToast.folders.slice(0, 4).map((folder, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setExtractedFoldersToast(null);
                      navigate(`/stories/${encodeURIComponent(folder)}`);
                    }}
                    className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full hover:bg-primary/20 transition-colors truncate max-w-[100px]"
                    data-testid={`folder-link-${idx}`}
                  >
                    {folder}
                  </button>
                ))}
                {extractedFoldersToast.folders.length > 4 && (
                  <span className="text-xs text-muted-foreground px-2 py-1">
                    +{extractedFoldersToast.folders.length - 4} more
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Leave Page Confirmation Dialog */}
      <AnimatePresence>
        {showLeaveConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            data-testid="leave-page-dialog"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full shadow-xl"
            >
              <h3 className="text-lg font-serif font-semibold text-foreground mb-2">Unsaved Changes</h3>
              <p className="text-sm text-muted-foreground mb-6">
                You have unsaved content. Are you sure you want to leave this page?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={cancelLeave}
                  className="flex-1 px-4 py-2 text-sm font-medium bg-secondary text-foreground rounded-xl hover:bg-secondary/80 transition-colors"
                  data-testid="stay-on-page-button"
                >
                  Stay
                </button>
                <button
                  onClick={confirmLeave}
                  className="flex-1 px-4 py-2 text-sm font-medium bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
                  data-testid="leave-page-button"
                >
                  Leave
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Welcome Back Popup */}
      <AnimatePresence>
        {showWelcomeBack && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => handleWelcomeDismiss('/')}
            data-testid="welcome-back-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="welcome-back-popup"
            >
              {/* Plant graphic */}
              <div className="flex justify-center mb-4">
                <div className="relative w-28 h-28">
                  <svg viewBox="0 0 120 120" className="w-full h-full plant-animated">
                    {/* Pot */}
                    <path d="M35 95 L45 110 L75 110 L85 95 Z" fill="var(--primary)" opacity="0.8" />
                    <rect x="30" y="88" width="60" height="8" rx="3" fill="var(--primary)" />
                    {/* Soil */}
                    <ellipse cx="60" cy="90" rx="28" ry="5" fill="hsl(25, 40%, 25%)" />
                    
                    {/* Dynamic stem and leaves based on streak - matching inline panel */}
                    {(!welcomeStreak || welcomeStreak.current_streak === 0) && (
                      <>
                        {/* Tiny nub barely poking out */}
                        <path d="M60 88 C60 84, 60 82, 60 78" stroke="var(--plant-sprout)" strokeWidth="2" fill="none" strokeLinecap="round" />
                        <motion.ellipse initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 }} cx="60" cy="77" rx="4" ry="3" fill="var(--plant-sprout)" opacity="0.7" />
                      </>
                    )}
                    
                    {welcomeStreak && welcomeStreak.current_streak >= 1 && welcomeStreak.current_streak < 3 && (
                      <>
                        {/* Small sprout - just popping out */}
                        <path d="M60 88 C60 78, 60 70, 60 62" stroke="var(--plant-leaf-light)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                        <motion.path initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 }} d="M60 72 C52 62, 38 66, 42 72 C38 64, 52 58, 60 72" fill="var(--plant-leaf-light)" />
                        <motion.path initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.4 }} d="M60 72 C68 62, 82 66, 78 72 C82 64, 68 58, 60 72" fill="var(--plant-leaf-dark)" />
                      </>
                    )}
                    
                    {welcomeStreak && welcomeStreak.current_streak >= 3 && welcomeStreak.current_streak < 7 && (
                      <>
                        {/* Taller plant with leaf pairs */}
                        <path d="M60 88 C60 68, 58 50, 60 35" stroke="var(--plant-stem)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                        <motion.path initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 }} d="M60 72 C50 62, 35 65, 40 72 C35 63, 50 56, 60 72" fill="var(--plant-leaf-light)" />
                        <motion.path initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.4 }} d="M60 72 C70 62, 85 65, 80 72 C85 63, 70 56, 60 72" fill="var(--plant-leaf-dark)" />
                        <motion.path initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5 }} d="M60 52 C48 42, 30 46, 36 52 C30 44, 48 38, 60 52" fill="var(--plant-leaf-accent)" />
                        <motion.path initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6 }} d="M60 52 C72 42, 90 46, 84 52 C90 44, 72 38, 60 52" fill="var(--plant-leaf-light)" />
                      </>
                    )}
                    
                    {welcomeStreak && welcomeStreak.current_streak >= 7 && (
                      <>
                        {/* Full plant with bloom */}
                        <path d="M60 88 C60 60, 56 40, 60 20" stroke="var(--plant-stem)" strokeWidth="3" fill="none" strokeLinecap="round" />
                        <motion.path initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 }} d="M60 68 C48 56, 32 60, 38 68 C32 58, 48 52, 60 68" fill="var(--plant-leaf-light)" />
                        <motion.path initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.4 }} d="M60 68 C72 56, 88 60, 82 68 C88 58, 72 52, 60 68" fill="var(--plant-leaf-dark)" />
                        <motion.path initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5 }} d="M60 48 C46 36, 26 40, 32 48 C26 38, 46 32, 60 48" fill="var(--plant-leaf-accent)" />
                        <motion.path initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6 }} d="M60 48 C74 36, 94 40, 88 48 C94 38, 74 32, 60 48" fill="var(--plant-leaf-light)" />
                        <motion.circle initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.7 }} cx="60" cy="16" r="10" fill="#fb7185" />
                        <motion.circle initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.75 }} cx="52" cy="14" r="6" fill="#f472b6" opacity="0.8" />
                        <motion.circle initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.8 }} cx="68" cy="14" r="6" fill="#f472b6" opacity="0.8" />
                        <motion.circle initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.85 }} cx="60" cy="8" r="6" fill="#f472b6" opacity="0.8" />
                        <motion.circle initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.9 }} cx="60" cy="16" r="4" fill="#fbbf24" />
                      </>
                    )}
                    
                    {/* Water droplets animation */}
                    <motion.circle
                      initial={{ y: 0, opacity: 0.8 }}
                      animate={{ y: 20, opacity: 0 }}
                      transition={{ repeat: Infinity, duration: 2, delay: 1 }}
                      cx="48" cy="55" r="2" fill="#60a5fa"
                    />
                    <motion.circle
                      initial={{ y: 0, opacity: 0.8 }}
                      animate={{ y: 20, opacity: 0 }}
                      transition={{ repeat: Infinity, duration: 2, delay: 1.5 }}
                      cx="72" cy="50" r="1.5" fill="#60a5fa"
                    />
                  </svg>
                </div>
              </div>

              {/* Streak count */}
              {welcomeStreak && welcomeStreak.current_streak > 0 ? (
                <>
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2, type: 'spring' }}
                    className="text-5xl font-bold text-foreground mb-1"
                    data-testid="streak-count-display"
                  >
                    {welcomeStreak.current_streak}
                  </motion.div>
                  <p className="text-sm text-muted-foreground mb-4">day growth streak</p>
                </>
              ) : isNewUser ? (
                <>
                  <h3 className="text-xl font-serif font-semibold text-foreground mb-2">Welcome to NOYO!</h3>
                  <p className="text-sm text-muted-foreground mb-4">Plant your first seed today</p>
                </>
              ) : (
                <>
                  <h3 className="text-xl font-serif font-semibold text-foreground mb-2">Welcome back!</h3>
                  <p className="text-sm text-muted-foreground mb-4">Add a story or journal to water your plant</p>
                </>
              )}

              {/* Weekly calendar */}
              {welcomeStreak && welcomeStreak.week_days && welcomeStreak.week_days.length > 0 && (
                <div className="flex justify-center gap-3 mb-4" data-testid="streak-week-calendar">
                  {welcomeStreak.week_days.map((day) => (
                    <div key={day.label} className="flex flex-col items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground font-medium">{day.label}</span>
                      {day.active ? (
                        <div className="w-7 h-7 rounded-lg bg-emerald-600/20 dark:bg-green-500/20 flex items-center justify-center">
                          <Check className="w-3.5 h-3.5 text-emerald-700 dark:text-green-500" />
                        </div>
                      ) : day.is_today ? (
                        <div className="w-7 h-7 rounded-lg border-2 border-dashed border-primary/40 flex items-center justify-center">
                          <span className="w-2 h-2 rounded-full bg-primary/40" />
                        </div>
                      ) : (
                        <div className="w-7 h-7 rounded-lg bg-muted/30 flex items-center justify-center">
                          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/20" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Motivational text */}
              {welcomeStreak && welcomeStreak.current_streak > 0 && (
                <p className="text-sm text-muted-foreground mb-4">
                  {welcomeStreak.current_streak >= 7 ? (
                    <>Your plant is <span className="text-emerald-700 dark:text-green-400 font-medium">flourishing</span>!</>
                  ) : welcomeStreak.current_streak >= 3 ? (
                    <>Your plant is <span className="text-emerald-700 dark:text-green-400 font-medium">growing</span>, keep it up!</>
                  ) : (
                    <>Your plant is <span className="text-emerald-700 dark:text-green-400 font-medium">sprouting</span>!</>
                  )}
                </p>
              )}

              {/* Navigation buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => handleWelcomeDismiss('/')}
                  className="flex-1 px-4 py-3 text-sm font-medium bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
                  data-testid="welcome-nav-journal"
                >
                  Journal
                </button>
                <button
                  onClick={() => handleWelcomeDismiss('/stories')}
                  className="flex-1 px-4 py-3 text-sm font-medium bg-secondary text-foreground rounded-xl hover:bg-secondary/80 transition-colors"
                  data-testid="welcome-nav-stories"
                >
                  Stories
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Theme Picker Popup — one-time for new & existing users */}
      <AnimatePresence>
        {showThemePicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleThemePickerDone}
            data-testid="theme-picker-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center"
              onClick={(e) => e.stopPropagation()}
              data-testid="theme-picker-popup"
            >
              <h3 className="text-lg font-serif font-semibold text-foreground mb-1">Choose your vibe</h3>
              <p className="text-sm text-muted-foreground mb-5">Pick a theme for your NOYO</p>
              <div className="flex items-center justify-center gap-5 mb-5">
                {[
                  { id: 'default', color: '#8DA399', label: 'Classic' },
                  { id: 'sunset', color: '#E07B39', label: 'Sunset' },
                  { id: 'twilight', color: '#9B6AA6', label: 'Twilight' },
                ].map(t => (
                  <button
                    key={t.id}
                    onClick={() => applyTheme(t.id)}
                    className="flex flex-col items-center gap-2"
                    data-testid={`theme-pick-${t.id}`}
                  >
                    <span
                      className={`w-12 h-12 rounded-full transition-all duration-200 ${pickedTheme === t.id ? 'scale-110' : 'opacity-50 hover:opacity-80 hover:scale-105'}`}
                      style={{
                        backgroundColor: t.color,
                        boxShadow: pickedTheme === t.id ? `0 0 0 3px var(--color-card-solid), 0 0 0 5px ${t.color}` : 'none'
                      }}
                    />
                    <span className={`text-xs font-medium ${pickedTheme === t.id ? 'text-foreground' : 'text-muted-foreground'}`}>{t.label}</span>
                  </button>
                ))}
              </div>
              <button
                onClick={handleThemePickerDone}
                className="w-full px-4 py-3 text-sm font-medium bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
                data-testid="theme-pick-done"
              >
                Done
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Guest signup modal — shown when guest tries to save */}
      {showGuestSignup && (
        <GuestSignupModal
          userName={user?.name || ''}
          onClose={() => setShowGuestSignup(false)}
          onUpgraded={handleGuestUpgraded}
        />
      )}
    </motion.div>
  );
}

export default JournalPage;
